import { clamp, projectPoint } from '../helpers';
import { KeeperAction, Phase, ShotLane, Vec3 } from '../types';
import { getSceneAsset, SceneAssetKey } from './assets';

interface SpriteOptions {
  auraColor?: string;
  flipX?: boolean;
  heightScale: number;
  offsetX?: number;
  offsetY?: number;
  rotation?: number;
  shadowScale?: number;
  widthScale?: number;
}

const KICK_WINDUP_MS = 78;
const KICK_CONTACT_MS = 150;
const KICK_FINISH_MS = 280;

const drawShadow = (ctx: CanvasRenderingContext2D, x: number, y: number, scale: number, rotation: number) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rotation);
  ctx.beginPath();
  ctx.fillStyle = 'rgba(2,6,23,0.26)';
  ctx.ellipse(0, 0, scale * 0.9, scale * 0.32, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

const drawSprite = (ctx: CanvasRenderingContext2D, width: number, height: number, position: Vec3, key: SceneAssetKey, options: SpriteOptions) => {
  const image = getSceneAsset(key);
  const projected = projectPoint(position, width, height);
  const drawX = projected.x + projected.scale * (options.offsetX ?? 0);
  const drawY = projected.y + projected.scale * (options.offsetY ?? 0);
  const spriteHeight = Math.max(48, projected.scale * options.heightScale);
  const spriteWidth = spriteHeight * (image ? image.width / image.height : 0.72) * (options.widthScale ?? 1);

  drawShadow(ctx, drawX, projected.y + spriteHeight * 0.02, spriteHeight * (options.shadowScale ?? 0.18), options.rotation ?? 0);
  if (options.auraColor) {
    const aura = ctx.createRadialGradient(drawX, drawY - spriteHeight * 0.56, spriteHeight * 0.08, drawX, drawY - spriteHeight * 0.56, spriteHeight * 0.72);
    aura.addColorStop(0, options.auraColor);
    aura.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = aura;
    ctx.fillRect(drawX - spriteWidth, drawY - spriteHeight * 1.6, spriteWidth * 2, spriteHeight * 1.8);
  }

  if (!image) {
    ctx.beginPath();
    ctx.fillStyle = '#34d399';
    ctx.arc(drawX, drawY - spriteHeight * 0.66, spriteHeight * 0.18, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  ctx.save();
  ctx.translate(drawX, drawY);
  ctx.rotate(options.rotation ?? 0);
  if (options.flipX) {
    ctx.scale(-1, 1);
  }
  ctx.drawImage(image, -spriteWidth * 0.5, -spriteHeight, spriteWidth, spriteHeight);
  ctx.restore();
};

const attackerSprite = (phase: Phase, phaseElapsedMs: number): SceneAssetKey => {
  if (phase === 'aim') {
    return 'attackerRunB';
  }

  if (phase === 'flight') {
    return phaseElapsedMs < KICK_WINDUP_MS ? 'attackerRunA' : 'attackerShoot';
  }

  if (phase === 'result') {
    return 'attackerShoot';
  }

  return Math.floor(phaseElapsedMs / 120) % 2 === 0 ? 'attackerRunA' : 'attackerRunB';
};

export const drawAttacker = (ctx: CanvasRenderingContext2D, width: number, height: number, position: Vec3, phase: Phase, phaseElapsedMs: number) => {
  const stride = Math.sin(phaseElapsedMs / 110);
  const windupProgress = clamp(phaseElapsedMs / KICK_WINDUP_MS, 0, 1);
  const contactProgress = clamp((phaseElapsedMs - KICK_WINDUP_MS) / (KICK_CONTACT_MS - KICK_WINDUP_MS), 0, 1);
  const finishProgress = easeOutKick(clamp((phaseElapsedMs - KICK_CONTACT_MS) / (KICK_FINISH_MS - KICK_CONTACT_MS), 0, 1));
  const kickArc = Math.sin(clamp((phaseElapsedMs - KICK_WINDUP_MS) / (KICK_FINISH_MS - KICK_WINDUP_MS), 0, 1) * Math.PI);
  drawSprite(ctx, width, height, position, attackerSprite(phase, phaseElapsedMs), {
    heightScale:
      phase === 'dribble'
        ? 2.44
        : phase === 'aim'
          ? 2.38
          : phase === 'flight'
            ? phaseElapsedMs < KICK_WINDUP_MS
              ? 2.4
              : 2.28
            : phase === 'result'
              ? 2.26
              : 2.34,
    offsetY:
      phase === 'dribble'
        ? -0.138 - Math.abs(stride) * 0.048
        : phase === 'aim'
          ? -0.122 - Math.abs(Math.sin(phaseElapsedMs / 180)) * 0.012
          : phase === 'flight'
            ? phaseElapsedMs < KICK_WINDUP_MS
              ? -0.122 - windupProgress * 0.014
              : -0.11 - Math.sin(contactProgress * Math.PI) * 0.022 - finishProgress * 0.01
            : phase === 'result'
              ? -0.11
              : -0.112,
    offsetX:
      phase === 'dribble'
        ? stride * 0.034
        : phase === 'aim'
          ? 0.01 + Math.sin(phaseElapsedMs / 180) * 0.008
          : phase === 'flight'
            ? phaseElapsedMs < KICK_WINDUP_MS
              ? 0.012 - windupProgress * 0.026
              : -0.014 - contactProgress * 0.048 - finishProgress * 0.028
            : phase === 'result'
              ? -0.06
              : 0,
    rotation:
      phase === 'dribble'
        ? -0.032
        : phase === 'aim'
          ? -0.038
          : phase === 'flight'
            ? phaseElapsedMs < KICK_WINDUP_MS
              ? -0.05 - windupProgress * 0.11
              : -0.24 + contactProgress * 0.34 + finishProgress * 0.05
            : phase === 'result'
              ? 0.08 - kickArc * 0.03
              : -0.015,
    shadowScale: phase === 'dribble' ? 0.18 : phase === 'flight' ? (phaseElapsedMs < KICK_WINDUP_MS ? 0.18 : 0.2) : 0.22,
    widthScale: 0.94,
  });
};

const easeOutKick = (value: number) => 1 - Math.pow(1 - value, 3);

export const drawKeeper = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  position: Vec3,
  flightProgress: number,
  keeperAction: KeeperAction | null,
  keeperLane: ShotLane | null,
) => {
  const isDiving = (keeperAction === 'save' || keeperAction === 'wrong-side') && keeperLane !== null && keeperLane !== 'center' && flightProgress > 0.16;
  drawSprite(ctx, width, height, position, isDiving ? 'keeperDiveLeft' : 'keeperSet', {
    auraColor: 'rgba(191,219,254,0.1)',
    heightScale: isDiving ? 1.02 : 2.58,
    offsetX: isDiving ? (keeperLane === 'left' ? -0.03 : 0.03) : 0,
    offsetY: isDiving ? -0.02 : keeperAction === 'late' ? -0.1 : -0.12,
    rotation: isDiving ? 0 : 0,
    flipX: keeperLane === 'right',
    shadowScale: isDiving ? 0.18 : 0.26,
    widthScale: isDiving ? 1 : 0.92,
  });
};
